import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import BasicPagination from 'components/BasicPagination';
import BasicCheckbox from 'components/BasicCheckbox';
import EditableContent from 'components/EditableContent';
import RemoveButton from 'components/RemoveButton';

import { StringHelpers } from 'helpers/string-helpers';

import './styles.css';

const FeedPostFormPreview = props => {
  const {
    data,
    data: {
      url,
      images,
      title,
      content,
      contentEdited,
      titleEdited,
      currentImageIdx = 0,
    },
    onRemove,
    onChangeText,
    onChangeTitle,
    onChangeImage,
  } = props;

  const [showImage, setShowImage] = useState(false);

  useEffect(() => {
    setShowImage(images && images.length > 0);
  }, [images]);

  const [EditableTitle, EditableText] = useMemo(
    () => [EditableContent('span'), EditableContent('p')],
    []
  );

  const editTitle = useMemo(
    () =>
      StringHelpers.unescape(
        titleEdited && titleEdited.length > 0 ? titleEdited : title
      ),
    [title, titleEdited]
  );

  const editContent = useMemo(
    () =>
      StringHelpers.unescape(
        contentEdited && contentEdited.length > 0 ? contentEdited : content
      ),
    [content, contentEdited]
  );

  const toggleImagePreview = () => {
    const show = !showImage;
    if (!show) {
      onChangeImage(-1);
    } else {
      onChangeImage(0);
    }
    setShowImage(show);
  };

  const changeThumbnail = page => {
    onChangeImage(page);
  };

  return data ? (
    <div className="feed-post-form-preview feed-post-form-preview--with-data">
      <div className="feed-post-form-preview__inner">
        <RemoveButton
          controlFunc={onRemove}
          customStyles={{
            top: '0',
            right: '0',
          }}
        />
        <div className="feed-post-form-preview__thumbnail">
          {showImage && (
            <div className="feed-post-form-preview__thumbnail-inner">
              <div className="feed-post-form-preview__thumbnail-image">
                <img
                  className="feed-post-form-preview__thumbnail-img"
                  src={images[currentImageIdx]}
                  alt={title}
                />
              </div>
            </div>
          )}

          <div className="feed-post-form-preview__thumbnail-controls">
            <div className="feed-post-form-preview__thumbnail__control">
              {images && images.length > 0 && (
                <BasicCheckbox
                  value={showImage}
                  singleRow
                  checked={showImage}
                  title="Show Image"
                  name="Show Image"
                  controlFunc={toggleImagePreview}
                />
              )}
            </div>
            {showImage && images.length > 1 && (
              <div className="feed-post-form-preview__thumbnail__control">
                <label className="feed-post-form-preview__thumbnail-label">
                  Choose a Thumbnail
                </label>
                <div className="feed-post-form-preview__thumbnail-pagination">
                  <BasicPagination
                    page={currentImageIdx}
                    perPage={1}
                    count={images.length}
                    controlFunc={changeThumbnail}
                    smallIcons
                    pageRangeDisplayed={3}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {content && (
          <div className="feed-post-form-preview__content">
            <div>
              <EditableTitle
                className="feed-post-form-preview__title"
                value={editTitle}
                onChangeValueHandler={onChangeTitle}
              />
            </div>

            <div>
              <a className="feed-post-form-preview__url" href={url}>
                {url}
              </a>
            </div>

            <div className="feed-post-form-preview__text-wrapper">
              <EditableText
                className="feed-post-form-preview__text"
                value={editContent}
                onChangeValueHandler={onChangeText}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  ) : null;
};

FeedPostFormPreview.propTypes = {
  data: PropTypes.shape({
    url: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    images: PropTypes.array.isRequired,
  }),
  onChangeImage: PropTypes.func.isRequired,
  onChangeTitle: PropTypes.func.isRequired,
  onChangeText: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

export default FeedPostFormPreview;
